/*** FOR REFERENCE https://github.com/remix-run/react-router/issues/8139 */

import * as React from 'react';
import type { History, Transition, Location } from 'history';

import {
	useNavigate,
	Navigator as BaseNavigator,
	UNSAFE_NavigationContext as NavigationContext,
} from 'react-router-dom';

interface Navigator extends BaseNavigator {
	block: History['block'];
}

type NavigationContextWithBlock = React.ContextType<typeof NavigationContext> & { navigator: Navigator };

type NavigationBlockerStates = 'Idle' | 'Blocked' | 'WaitingToResume' | 'Resumed';

type NavigationBlockerContext = {
	state: NavigationBlockerStates;

	block: (location: Location) => void;
	resume: () => void;
	waitToResume: () => void;
	cancel: () => void;
	reset: () => void;

	setBlockedNavigationLocation: React.Dispatch<React.SetStateAction<Location | null>>;
};

const Context = React.createContext<NavigationBlockerContext | null>(null);

export const NavigationBlockerProvider: React.FC = ({ children }) => {
	const navigate = useNavigate();

	const [state, setState] = React.useState<NavigationBlockerStates>('Idle');

	const [blockedNavigationLocation, setBlockedNavigationLocation] = React.useState<Location | null>(null);

	const block = (location: Location) => {
		setState('Blocked');
		setBlockedNavigationLocation(location);
	};

	const waitToResume = () => {
		if (state === 'Blocked') setState('WaitingToResume');
	};

	const resume = () => {
		if (state === 'Blocked' || state === 'WaitingToResume') setState('Resumed');
	};

	const reset = () => {
		setState('Idle');
		setBlockedNavigationLocation(null);
	};

	React.useEffect(() => {
		if (state === 'Resumed' && blockedNavigationLocation !== null) {
			setState('Idle');
			setBlockedNavigationLocation(null);
			navigate(blockedNavigationLocation.pathname);
		}
	}, [state, blockedNavigationLocation, navigate, setState, setBlockedNavigationLocation]);

	return (
		<Context.Provider
			value={{
				state,

				block,
				resume,
				waitToResume,
				cancel: reset,
				reset,

				setBlockedNavigationLocation,
			}}
		>
			{children}
		</Context.Provider>
	);
};

export function useNavigationBlockerContext() {
	var context = React.useContext(Context);

	if (context == null)
		throw Error('You tried to consume the NavigationBlockerContext outside of an NavigationBlockerProvider!');

	return context;
}

/**
 * @source https://github.com/remix-run/react-router/commit/256cad70d3fd4500b1abcfea66f3ee622fb90874
 */
export function useBlocker(when = true) {
	const { state, block, reset } = useNavigationBlockerContext();
	const { navigator } = React.useContext(NavigationContext) as NavigationContextWithBlock;

	React.useEffect(() => {
		if (state === 'Resumed') {
			reset();
			return;
		}

		if (!when) {
			return;
		}

		const unblock = navigator.block((transition: Transition) => {
			block(transition.location);
		});

		return unblock;
	}, [navigator, when, state, block, reset]);
}
